/**
 * WHICH DEVICE THE PERSON LAST USED, so the focus ring can be what Apple says it is.
 *
 * THE RULE (SH-A3, the owner's click-through of 2026-09-10). A focus ring is the
 * KEYBOARD indicator, not a click result: "In general, use a focus ring for a text or
 * search field, but use a highlight in a list or collection"
 * (https://developer.apple.com/design/human-interface-guidelines/focus-and-selection).
 * After a mouse click a sidebar row shows its selection highlight and a segment its On
 * state, and neither shows a ring. The web spelling of the same heuristic is
 * `:focus-visible`, which "allows web authors to style the focus indicator only if it
 * would be drawn natively"
 * (https://webkit.org/blog/12179/the-focus-indicated-pseudo-class-focus-visible/).
 *
 * WHY THE BROWSER'S OWN HEURISTIC IS NOT ENOUGH. It is right about the click itself -
 * measured on this build, a clicked sidebar row and a clicked segment both report
 * `:focus-visible` false - and wrong about what comes after it: the NEXT keydown of any
 * kind promotes the element that a mouse put focus on. The owner pressed Command-Shift-4
 * to take a screenshot and the row he had clicked grew a ring (owner-04), and the same
 * appears on the segmented control (owner-42, owner-53, owner-34 to 36). A screenshot
 * chord is not an interaction with a control.
 *
 * WHAT SWITCHES THE MODALITY, and why it is a list rather than "any key". Keyboard means
 * the keyboard is MOVING something: Tab and Shift-Tab between focus groups, the arrows
 * inside one, Home/End/PageUp/PageDown, and Enter or Space, which activate the thing the
 * keyboard is standing on (rulebook D7, D8; HIG Focus and selection). A key held with
 * Command or Control is an app or system command - the screenshot chord, Command-K,
 * Command-S - and moves no focus, so it leaves the modality where it was. Everything else
 * (typing) needs no ring policy, because text and search fields keep their ring in every
 * modality, which is Apple's own split.
 *
 * ESCAPE IS NOT ON THE LIST, AND THAT IS THE CORRECTION OF WAVE 4 ROUND 2 (item 10, Parent
 * B's captures `row-hover-1440`, `focus-after-click-1440`, `pane-empty-1280`). Escape
 * DISMISSES: it closes a popover, a menu or a sheet and moves focus nowhere. Counting it
 * as keyboard navigation meant that closing a panel a MOUSE had opened rewrote the
 * document's modality, and the toolbar item the mouse had focused then kept its ring
 * through every later click on a row - which is the ring the owner and Parent B both saw
 * and which Apple does not draw ("use a focus ring for a text or search field, but use a
 * highlight in a list or collection", hig:
 * https://developer.apple.com/design/human-interface-guidelines/focus-and-selection).
 * Verified in the running browser on 4e5b348: after clicking the sidebar's search field
 * and pressing Escape, `document.documentElement.dataset.inputModality` read `keyboard`.
 * A person who is really navigating by keyboard reaches the next control with Tab, which
 * sets it back on the same keystroke.
 *
 * The state is one attribute on `<html>`; `index.css` reads it. Nothing here draws.
 */

/** Keys that MOVE or ACTIVATE focus. Anything else is typing, a command, or a dismissal. */
const NAVIGATION_KEYS = new Set([
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Enter',
  ' ',
]);

export type InputModality = 'pointer' | 'keyboard';

export function setInputModality(modality: InputModality): void {
  document.documentElement.dataset['inputModality'] = modality;
}

/**
 * Starts listening and returns the teardown. Both listeners are on the capture phase so a
 * component that stops propagation cannot leave the document lying about what happened.
 */
export function startInputModality(): () => void {
  const onPointer = () => setInputModality('pointer');
  const onKey = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey) return;
    if (!NAVIGATION_KEYS.has(event.key)) return;
    setInputModality('keyboard');
  };
  document.addEventListener('pointerdown', onPointer, true);
  document.addEventListener('keydown', onKey, true);
  return () => {
    document.removeEventListener('pointerdown', onPointer, true);
    document.removeEventListener('keydown', onKey, true);
  };
}
